import {
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { GcpNetworkConnectivityConfig } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { BottomDialog } from '@/components/dialogs/bottomDialog';
import { PanelFooter } from '@/components/panels/panelFooter';
import { PanelHeader } from '@/components/panels/panelHeader';
import { GcpSubnetByNameSelect } from '@/components/regions/gcpSubnetSelect';
import { ReadMoreLink } from '@/components/shared/readMoreLink';

export const GcpNetworkConfigurationDialog = ({
  accountId,
  config: initialConfig,
  regions,
  onSave,
  onCancel,
}: {
  accountId: string;
  config: GcpNetworkConnectivityConfig | undefined;
  regions: string[];
  onSave: (config: GcpNetworkConnectivityConfig) => void;
  onCancel: () => void;
}) => {
  const [config, setConfig] = useState<
    GcpNetworkConnectivityConfig | undefined
  >(initialConfig ? { ...initialConfig } : undefined);

  const submitDisabled = () => {
    if (!config) {
      return true;
    }

    if (!config.subnetsPerRegion?.length) {
      return true;
    }

    if (config.subnetsPerRegion?.some((x) => !x.region || !x.subnetName)) {
      return true;
    }

    return false;
  };

  const onSaveClick = () => {
    if (config) {
      config.subnetsPerRegion = config.subnetsPerRegion?.filter(
        (x) => x.region && x.subnetName
      );

      onSave(config);
    }
  };

  useEffect(() => {
    setConfig(initialConfig ? { ...initialConfig } : undefined);
  }, [initialConfig]);

  return (
    <BottomDialog open={!!config}>
      <Stack className='h-full'>
        <PanelHeader
          onClose={onCancel}
          title='Customize Network'
          subtitle={
            <Stack className='mx-[84px] my-[24px]' gap='24px' flexGrow={1}>
              <Stack direction='row' gap='4px'>
                <Typography component='span' fontWeight={300}>
                  Customizing settings for
                </Typography>
                <Typography component='span' className='font-semibold'>
                  {`${config?.network}.`}
                </Typography>
              </Stack>
              <Typography
                className='w-[700px]'
                fontWeight={300}
                lineHeight={'20px'}
              >
                {`Define the subnets you want Eon to use. `}
                <ReadMoreLink href='/docs/user-guide/configuration/connect-your-cloud-accounts/restore-accounts/manage-restore-accounts.mdx' />
              </Typography>
            </Stack>
          }
        />
        <Stack
          gap='12px'
          className='py-[16px] px-[24px] overflow-y-auto'
          flexGrow={1}
        >
          <Stack className='mx-[60px] mt-[24px]' gap='24px' flexGrow={1}>
            <Stack direction='row' gap='100px'>
              <Stack gap='36px' className='w-[240px] pt-[8px]'>
                <Typography className='font-semibold'>
                  Subnets per region
                </Typography>
                <Typography lineHeight={'20px'} fontWeight={300}>
                  {`The restore server will be launched in the subnets you specify here. Make sure outbound access is open in the selected subnets.`}
                </Typography>
              </Stack>
              <Stack
                flexGrow={1}
                gap='24px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                {regions.sort().map((region) => {
                  const subnetName = config?.subnetsPerRegion?.find(
                    (y) => y.region === region
                  )?.subnetName;

                  return (
                    <Stack
                      key={region}
                      direction='row'
                      alignItems='center'
                      gap='40px'
                      className='h-[33px]'
                    >
                      <Typography className='w-[150px]'>{region}</Typography>
                      <FormControlLabel
                        labelPlacement='start'
                        control={<Switch className='ml-[12px]' />}
                        label='Customize'
                        checked={subnetName !== undefined}
                        onChange={(event, checked) => {
                          setConfig((prev) => {
                            if (!prev) {
                              return prev;
                            }

                            const newState: GcpNetworkConnectivityConfig = {
                              ...prev,
                              subnetsPerRegion: prev.subnetsPerRegion?.filter(
                                (y) => y.region !== region
                              ),
                            };

                            if (checked) {
                              if (!newState.subnetsPerRegion) {
                                newState.subnetsPerRegion = [];
                              }

                              newState.subnetsPerRegion?.push({
                                region,
                                subnetName: '',
                              });
                            }

                            return newState;
                          });
                        }}
                      />
                      <Typography
                        color={
                          subnetName === undefined
                            ? 'var(--mui-palette-text-disabled)'
                            : undefined
                        }
                        width={'180px'}
                        textAlign={'right'}
                      >
                        {subnetName === undefined
                          ? 'Use default subnet'
                          : 'Use subnet:'}
                      </Typography>
                      {subnetName !== undefined && (
                        <Stack className='w-[250px]'>
                          <GcpSubnetByNameSelect
                            accountId={accountId}
                            region={region}
                            subnetName={subnetName}
                            networkName={config?.network}
                            onChange={(subnetName) => {
                              setConfig((prev) => {
                                if (!prev || !subnetName) {
                                  return prev;
                                }

                                const newState: GcpNetworkConnectivityConfig = {
                                  ...prev,
                                  subnetsPerRegion: prev.subnetsPerRegion?.map(
                                    (y) => {
                                      if (y.region === region) {
                                        return {
                                          ...y,
                                          subnetName,
                                        };
                                      }

                                      return y;
                                    }
                                  ),
                                };

                                return newState;
                              });
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <PanelFooter
          primaryButton={{
            text: 'Save Settings',
            onClick: onSaveClick,
            disabled: submitDisabled(),
            'data-testid': 'save-custom-vpc',
          }}
          secondaryButton={{
            text: 'Cancel',
            onClick: onCancel,
            'data-testid': 'cancel-custom-vpc',
          }}
        />
      </Stack>
    </BottomDialog>
  );
};
